import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createMember,
  deleteMember,
  fetchMembers,
  resetMemberPassword,
  setMemberRole,
} from '../api';
import type { MemberInfo } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after any change so the dashboard can refresh the view options. */
  onChanged: () => void;
}

/**
 * Family management (head only): create members with username/password,
 * reset passwords, remove members. Each member sees only their own tagged
 * transactions; the head sees the family-wide view.
 */
export function MembersModal({ open, onClose, onChanged }: Props) {
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Add-member form
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Inline reset-password panel (per member)
  const [resetFor, setResetFor] = useState<MemberInfo | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchMembers();
      if (res.ok) setMembers(res.members);
      else setError(res.error ?? 'Failed to load members');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members');
    }
  }, []);

  useEffect(() => {
    if (open) {
      setError(null);
      setMessage(null);
      void load();
    }
  }, [open, load]);

  if (!open) return null;

  const addMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Enter a username and password for the member.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await createMember(name.trim(), username.trim(), password);
      if (!res.ok || !res.member) {
        setError(res.error ?? 'Failed to create member');
        return;
      }
      setName('');
      setUsername('');
      setPassword('');
      setMessage(`✅ Added ${res.member.name} — they can log in with "${res.member.username}".`);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create member');
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (m: MemberInfo) => {
    if (resetPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setResetBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await resetMemberPassword(m.id, resetPassword);
      if (!res.ok) {
        setError(res.error ?? 'Failed to reset password');
        return;
      }
      setMessage(`✅ Password reset for ${m.name}.`);
      setResetFor(null);
      setResetPassword('');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset password');
    } finally {
      setResetBusy(false);
    }
  };

  const doDelete = async (m: MemberInfo) => {
    if (!window.confirm(`Remove ${m.name} (${m.username})? Their past transactions stay in the family records, but they lose access.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await deleteMember(m.id);
      if (!res.ok) {
        setError(res.error ?? 'Failed to remove member');
        return;
      }
      setMessage(`✅ Removed ${m.name}.`);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove member');
    } finally {
      setBusy(false);
    }
  };

  const doSetRole = async (m: MemberInfo, role: 'co-head' | 'member') => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await setMemberRole(m.id, role);
      if (!res.ok) {
        setError(res.error ?? 'Failed to update role');
        return;
      }
      setMessage(
        role === 'co-head'
          ? `✅ ${m.name} is now a co-head — they can manage members and see the family view.`
          : `✅ ${m.name} is back to a regular member.`,
      );
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update role');
    } finally {
      setBusy(false);
    }
  };

  const roleBadge = (role: string) =>
    role === 'head'
      ? 'bg-zinc-700/30 text-zinc-300'
      : role === 'co-head'
        ? 'bg-amber-600/30 text-amber-300'
        : 'bg-slate-700 text-slate-300';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-slate-950/70 p-0 sm:items-center sm:p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-lg flex-col rounded-t-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Family members"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-100">👥 Family members</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-slate-200"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p className="mb-4 text-sm text-slate-400">
          Each member gets their own username &amp; password and sees only their
          own spending; you (the head) see the family-wide view and can switch
          per-member from the dashboard.
        </p>

        {(error || message) && (
          <p className={`mb-3 rounded-lg border px-3 py-2 text-xs ${error ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'}`}>
            {error ?? message}
          </p>
        )}

        {/* Member list */}
        <div className="mb-4 space-y-2 overflow-y-auto pr-1">
          {members.length === 0 && (
            <p className="rounded-xl border border-dashed border-slate-700 p-4 text-center text-sm text-slate-500">
              No members yet — add your first family member below.
            </p>
          )}
          {members.map((m) => (
            <div key={m.id} className="rounded-xl border border-slate-700/70 bg-slate-800/50 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-100">
                    {m.name}
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${roleBadge(m.role)}`}>
                      {m.role === 'co-head' ? 'Co-head' : m.role}
                    </span>
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    @{m.username} · view tag: <code className="text-zinc-300">{m.tag}</code>
                  </p>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {m.role !== 'head' && (
                    <button
                      type="button"
                      onClick={() =>
                        void doSetRole(m, m.role === 'member' ? 'co-head' : 'member')
                      }
                      disabled={busy}
                      title={
                        m.role === 'member'
                          ? 'Grant admin rights (family view + member management)'
                          : 'Remove admin rights'
                      }
                      className="rounded-lg border border-slate-600 px-2 py-1 text-xs font-semibold text-slate-300 transition hover:bg-slate-700 disabled:opacity-50"
                    >
                      {m.role === 'member' ? 'Make co-head' : 'Make member'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setResetFor(resetFor?.id === m.id ? null : m);
                      setResetPassword('');
                      setError(null);
                    }}
                    className="rounded-lg border border-slate-600 px-2 py-1 text-xs font-semibold text-slate-300 transition hover:bg-slate-700"
                  >
                    {resetFor?.id === m.id ? 'Close' : 'Reset password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void doDelete(m)}
                    disabled={busy}
                    className="rounded-lg border border-rose-600/40 px-2 py-1 text-xs font-semibold text-rose-300 transition hover:bg-rose-600/20 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>

              {resetFor?.id === m.id && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    autoFocus
                    className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
                  />
                  <button
                    type="button"
                    onClick={() => void doReset(m)}
                    disabled={resetBusy}
                    className="shrink-0 rounded-lg bg-zinc-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-zinc-600 disabled:opacity-50"
                  >
                    {resetBusy ? '…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add-member form */}
        <form onSubmit={addMember} className="border-t border-slate-700/70 pt-4">
          <p className="mb-2 text-sm font-semibold text-slate-200">➕ Add a member</p>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (e.g. Priya)"
              className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
            />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username (e.g. priya)"
              className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
            />
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Temporary password (min 6 chars)"
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
            />
            <button
              type="submit"
              disabled={busy}
              className="shrink-0 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
            >
              {busy ? '…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
